<template>
  <hr v-if="availableTimeslots?.length > 0" />
  <div class="consultation-booking" v-if="availableTimeslots?.length > 0">
    <!-- Header section with icon -->
    <div class="consultation-header" v-if="!showSuccessMessage">
      <div class="consultation-header__icon">
        <IconInsurance aria-hidden="true" />
      </div>
      <div class="consultation-header__content">
        <h3 class="h4">
          Zeit und Kosten sparen - Versicherungsberatungstermin hinzufügen
        </h3>
        <p class="consultation__intro">
          Nutzen Sie Ihre Wartezeit während Ihres Überprüfungstermins und lassen Sie sich in dieser
          Zeit von unseren Versicherungsexpert:innen beraten.
        </p>
      </div>
    </div>

    <!-- Timeslots display -->
    <div
      v-if="!isTimeslotsLoading && availableTimeslots.length > 0 && !showSuccessMessage"
      class="timeslots"
    >
      <h3 class="timeslots__header h5" id="timeslots-heading">
        Verfügbare Beratungstermine während Ihres regulären Termins (bitte gewünschten Termin
        auswählen):
      </h3>

      <!-- Group by date -->
      <div
        v-for="(slots, date) in timeslotsByDate"
        :key="date"
        class="date-group"
        role="group"
        :aria-labelledby="`date-heading-${date}`"
      >
        <h4 class="date-group__header" :id="`date-heading-${date}`">
          {{ formatDateTime('day, dateLong', new Date(date)) }}
        </h4>

        <div
          class="timeslot-list"
          role="group"
          :aria-labelledby="`date-heading-${date}`"
          aria-describedby="timeslots-heading"
        >
          <button
            v-for="slot in slots"
            :key="`${slot.startsAt}-${slot.endsAt}`"
            :class="['timeslot-button', { selected: isSelected(slot) }]"
            :aria-pressed="isSelected(slot)"
            :aria-label="`Zeitslot ${formatDateTime(
              'timeFrom - timeUntil',
              new Date(slot.startsAt),
              new Date(slot.endsAt)
            )} Uhr${isSelected(slot) ? ' ausgewählt' : ' verfügbar'}`"
            @click="selectTimeslot(slot)"
          >
            {{
              formatDateTime('timeFrom - timeUntil', new Date(slot.startsAt), new Date(slot.endsAt))
            }}
            Uhr
          </button>
        </div>
      </div>
    </div>

    <!-- Booking confirmation dialog -->
    <el-dialog
      v-model="shouldShowDialog"
      :show-close="false"
      custom-class="confirmation"
      append-to-body
      :aria-labelledby="'dialog-title'"
      :aria-describedby="'dialog-subtitle'"
      role="dialog"
      aria-modal="true"
    >
      <ConsultationBookingDialog
        :selectedTimeslot="selectedTimeslot"
        :isBookingInProgress="isBookingInProgress"
        :hasError="hasError"
        @closeDialog="closeBookingDialog"
        @bookConsultation="bookConsultation"
      />
    </el-dialog>

    <!-- Success message -->
    <template v-if="showSuccessMessage">
      <div class="success-message" role="status" aria-live="polite" aria-labelledby="success-title">
        <h3 id="success-title" class="h4 success-message-title">Ihr Beratungstermin wurde erfolgreich gebucht!</h3>
        <p>
          Datum:
          <strong>{{
            formatDateTime('day, dateLong, timeFrom Uhr', new Date(currentBooking.startsAt))
          }}</strong>
        </p>
        <p>Eine Bestätigung wurde an Ihre E-Mail-Adresse gesendet.</p>
      </div>
      <div class="success-message__actions">
        <Button label="In Kalender exportieren" outline @clicked="exportOtbToCalendar" />
      </div>
    </template>
  </div>
</template>

<script setup>
import ConsultationBookingDialog from '@/components/Booking/ConsultationBookingDialog.vue'
import Button from '@/components/Button.vue'
import IconInsurance from '@/components/icons/IconInsurance.vue'
import { formatDateTime } from '@/utils/format'
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

// Component props

// Store access
const store = useStore()

// Local state
const showBookingDialog = ref(false)

// Computed property to control dialog visibility
const shouldShowDialog = computed({
  get: () => {
    const shouldShow = showBookingDialog.value && !!selectedTimeslot.value
    return shouldShow
  },
  set: (value) => {
    if (!value) {
      closeBookingDialog()
    } else {
      showBookingDialog.value = value
    }
  },
})

const showSuccessMessage = computed(() => {
  return bookingSuccess.value && currentBooking.value
})

// State mappings
const selectedTimeslot = computed(() => store.state.otbAppointment.selectedTimeslot)
const isTimeslotsLoading = computed(() => store.state.otbAppointment.isTimeslotsLoading)
const isBookingInProgress = computed(() => store.state.otbAppointment.isBookingInProgress)
const currentBooking = computed(() => store.state.otbAppointment.currentBooking)
const bookingSuccess = computed(() => store.state.otbAppointment.bookingSuccess)

// Getter mappings
const availableTimeslots = computed(() => store.getters['otbAppointment/availableTimeslots'])
const timeslotsByDate = computed(() => store.getters['otbAppointment/timeslotsByDate'])
const hasError = computed(() => store.getters['otbAppointment/hasError'])

// Methods
const loadTimeslots = () => {
  store.dispatch('otbAppointment/fetchAvailableTimeslots')
}

const selectTimeslot = (timeslot) => {
  // Clear any existing errors before selecting new timeslot
  store.dispatch('otbAppointment/clearError')
  // Select the timeslot
  store.dispatch('otbAppointment/selectTimeslot', timeslot)
  // Open dialog when a timeslot is selected
  showBookingDialog.value = true
}

const closeBookingDialog = () => {
  // Clear any existing errors when closing the dialog
  store.dispatch('otbAppointment/clearError')
  // Clear the selected timeslot when dialog is closed
  store.dispatch('otbAppointment/clearSelectedTimeslot')
  // Force close the dialog
  showBookingDialog.value = false
}

const isSelected = (slot) => {
  if (!selectedTimeslot.value) return false
  return (
    selectedTimeslot.value.startsAt === slot.startsAt &&
    selectedTimeslot.value.endsAt === slot.endsAt
  )
}

const bookConsultation = async () => {
  try {
    // We get most data from the store through the action
    // Just add any component-specific data here
    await store.dispatch('otbAppointment/bookConsultation')

    // Dialog closing is handled by the watcher on bookingSuccess
    // If there's an error, the dialog stays open to show the error message
  } catch (error) {
    console.error('Booking error:', error)
    // Dialog stays open to show error message
  }
}

const exportOtbToCalendar = () => {
  if (currentBooking.value?.id) {
    store.dispatch('exportOtbAppointment', currentBooking.value.id)
  }
}

// Lifecycle hooks
onMounted(() => {
  loadTimeslots()
})

// Watch for successful booking to close dialog
watch(bookingSuccess, (newValue) => {
  if (newValue && shouldShowDialog.value) {
    shouldShowDialog.value = false
  }
})
</script>

<style scoped lang="scss">
.consultation-booking {
  .consultation-header {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
  }
  .consultation-header__icon {
    @include flex-center;
    flex: 0 0 56px;
    background-color: $color-yellow-primary;
    border-radius: 50%;
    margin-right: 24px;
    height: 56px;

    @media (max-width: $breakpoint-max-mobile) {
      flex: 0 0 40px;
      margin-right: 16px;
      height: 40px;

      svg {
        height: 24px;
        width: 24px;
      }
    }
  }

  .consultation-header__content {
    flex: 1;

    .h4 {
      margin-bottom: 8px;
    }
  }

  .consultation__intro {
    color: $color-text-secondary;
  }

  .success-message {
    padding: 15px;
    border-radius: 4px;
    background-color: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #c8e6c9;

		&-title {
			margin-bottom: 8px;
		}
    &__actions {
      margin-top: 8px;
      display: flex;
      justify-content: flex-start;
    }
  }

  .date-group {
    padding: 12px;
    border: 1px solid $color-grey-light;
    border-radius: 4px;
  }

  .date-group__header {
    margin-bottom: 8px;
  }

  .timeslots {
    margin-top: 16px;
  }

  .timeslots__header {
    margin-bottom: 8px;
  }

  .timeslot-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    @media (max-width: $breakpoint-max-mobile) {
      display: grid;
    }
  }

  .date-group__header {
    margin-bottom: 12px;
  }

  .timeslot-button {
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 4px;
    background-color: $color-grey-light;
    transition: all 0.2s;
    border: 2px solid transparent;

    &:hover,
    &:focus {
      background-color: $color-grey-dark;
    }

    &:focus {
      outline: 2px solid $color-yellow-primary;
      outline-offset: 2px;
    }

    &.selected {
      background-color: $color-yellow-primary;

      &:focus {
        outline-color: $color-grey-dark;
      }
    }

    &[aria-pressed='true'] {
      font-weight: 600;
    }
  }
}
</style>
